import type { TFunction } from 'i18next';
import { isPasswordPolicyRule, type PasswordPolicyRule } from './password-policy';
import { readApiError } from '@shared/api/errors/read-api-error';
import { formatDateTime, retryAfterMinutes } from '@shared/lib/format-date-time';

export interface PasswordApiFailure {
  code?: string;
  policyRules: PasswordPolicyRule[];
  fieldErrors: Record<string, string>;
  retryAfterSec?: number;
  lockedUntil?: string;
  showOtp: boolean;
  invalidLink: boolean;
}

export const mapPasswordApiError = (error: unknown): PasswordApiFailure | null => {
  const parsed = readApiError(error);
  if (!parsed) {
    return null;
  }

  return {
    code: parsed.code,
    policyRules: parsed.rules.filter(isPasswordPolicyRule),
    fieldErrors: parsed.fieldErrors,
    retryAfterSec: parsed.retryAfterSec,
    lockedUntil: parsed.lockedUntil,
    showOtp: parsed.code === 'OTP_REQUIRED' || parsed.code === 'OTP_INVALID',
    invalidLink: parsed.code === 'RESET_TOKEN_INVALID',
  };
};

export const passwordErrorMessage = (
  failure: PasswordApiFailure,
  t: TFunction,
  locale: string,
): string | null => {
  switch (failure.code) {
    case 'RESET_TOKEN_INVALID':
      return t('recovery.invalidLink');
    case 'OTP_INVALID':
      return t('recovery.otpInvalid');
    case 'CURRENT_PASSWORD_INVALID':
      return t('recovery.currentInvalid');
    case 'PASSWORD_SAME':
      return t('recovery.samePassword');
    case 'TOO_MANY_REQUESTS':
      return t('recovery.tooManyRequests', {
        minutes: retryAfterMinutes(failure.retryAfterSec ?? 60),
      });
    case 'WITHDRAWALS_LOCKED':
      return t('withdrawal.lock.until', {
        ns: 'cabinet',
        datetime: formatDateTime(failure.lockedUntil ?? '', locale),
      });
    case 'PASSWORD_POLICY':
      return null;
    default:
      return null;
  }
};
